import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, Output, Input, ViewEncapsulation, Component, NgModule } from '@angular/core';
import { Validators, NG_VALUE_ACCESSOR, NG_VALIDATORS, FormsModule } from '@angular/forms';
import * as i3 from '@engineering11/ui-lib/e11-icon';
import { E11IconModule } from '@engineering11/ui-lib/e11-icon';
import * as i2 from '@engineering11/ui-lib/e11-input-label';
import { E11InputLabelModule } from '@engineering11/ui-lib/e11-input-label';
import * as i4 from '@engineering11/ui-lib/e11-selector';
import { E11SelectorModule } from '@engineering11/ui-lib/e11-selector';

class E11MultiSelectorComponent {
    set value(value) {
        this.selectedOptions = value ?? [];
    }
    constructor() {
        this.id = '';
        this.name = '';
        this.labelClassOverrides = '';
        this.inputClass = '';
        this.containerClass = '';
        this.options = [];
        this.hasError = false;
        this.required = false;
        this.disabled = false;
        this.loading = false;
        this.multi = true;
        this.labelTextOptional = '(Optional)';
        this.color = 'primary-accent';
        this.valueChange = new EventEmitter();
        this.selectedOptions = [];
    }
    ngOnInit() {
        this.checkRequired();
    }
    toggleOption(option) {
        this.hasError = false;
        if (this.hasOption(option)) {
            this.remove(option);
        }
        else {
            this.add(option);
        }
        this.handleValueChange(this.selectedOptions);
    }
    hasOption(option) {
        return this.selectedOptions.includes(option.value);
    }
    remove(option) {
        this.selectedOptions = this.selectedOptions.filter((value) => value !== option.value);
    }
    add(option) {
        this.selectedOptions = this.multi ? [...this.selectedOptions, option.value] : [option.value];
    }
    getOptionLabel(value) {
        return this.options?.find((item) => item.value === value)?.label || '';
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = this.disabled || isDisabled;
    }
    validate(control) {
        if (control.hasValidator(Validators.required) && (control.value?.length == 0 || control.value === null)) {
            this.hasError = this.hasError ? true : control.touched;
            return { required: true };
        }
        this.hasError = false;
        return null;
    }
    checkRequired() {
        if (this.parentForm && this.formControlName) {
            const formControl = this.parentForm.controls[this.formControlName];
            this.required = formControl.hasValidator(Validators.required);
        }
    }
    handleValueChange(selectedOptions) {
        if (this.onChange) {
            this.onTouched();
            this.onChange(selectedOptions);
        }
        this.valueChange.emit(selectedOptions);
        if (!this.onChange && this.required && !selectedOptions.length) {
            this.hasError = true;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MultiSelectorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11MultiSelectorComponent, selector: "e11-multi-selector", inputs: { id: "id", name: "name", label: "label", labelClassOverrides: "labelClassOverrides", inputClass: "inputClass", containerClass: "containerClass", parentForm: "parentForm", formControlName: "formControlName", options: "options", value: "value", hasError: "hasError", required: "required", disabled: "disabled", loading: "loading", multi: "multi", tooltipConfig: "tooltipConfig", labelTextOptional: "labelTextOptional", color: "color" }, outputs: { valueChange: "valueChange" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11MultiSelectorComponent),
                multi: true,
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => E11MultiSelectorComponent),
                multi: true,
            },
        ], ngImport: i0, template: `<div class="e11-relative {{ containerClass }} ">
    <e11-input-label
      [for]="id"
      [label]="label"
      [required]="required"
      [disabled]="disabled"
      [hasError]="hasError"
      [labelClassOverrides]="labelClassOverrides"
      [tooltipConfig]="tooltipConfig"
      [labelTextOptional]="labelTextOptional"
    ></e11-input-label>

    <div class="e11-flex e11-flex-wrap e11-gap-2 {{ inputClass }}">
      <ng-container *ngFor="let option of options">
        <e11-selector
          [color]="color"
          [loading]="loading"
          [hasCheckmark]="true"
          [label]="option.label"
          [selected]="hasOption(option)"
          [disabled]="option.disabled || disabled"
          [disableDisabledCursor]="loading ? true : false"
          (toggled)="toggleOption(option)"
          class="e11-cursor-pointer"
          [ngClass]="{
            'e11-skeleton e11-cursor-wait': loading,
          }"
        ></e11-selector>
      </ng-container>
    </div>

    <div *ngIf="hasError" class="e11-absolute e11-inset-y-0 e11-right-0 e11-pr-3 e11-flex e11-items-start e11-pointer-events-none">
      <e11-icon-error></e11-icon-error>
    </div>
  </div>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.E11InputLabelComponent, selector: "e11-input-label", inputs: ["label", "id", "for", "size", "labelClassOverrides", "required", "hasError", "disabled", "darkMode", "requiredMode", "labelTextOptional", "tooltipConfig"] }, { kind: "component", type: i3.E11IconErrorComponent, selector: "e11-icon-error", inputs: ["classOverrides"] }, { kind: "component", type: i4.E11SelectorComponent, selector: "e11-selector", inputs: ["icon", "color", "shape", "size", "tileStyle", "type", "label", "selected", "hasCheckmark", "disabled", "disableDisabledCursor", "selectedIcon", "selectedIconSize", "loading", "containerClassOverrides", "containerClass", "contentClassOverrides", "contentClass", "labelClassOverrides", "labelClass"], outputs: ["toggled", "onToggle"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MultiSelectorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-multi-selector',
                    template: `<div class="e11-relative {{ containerClass }} ">
    <e11-input-label
      [for]="id"
      [label]="label"
      [required]="required"
      [disabled]="disabled"
      [hasError]="hasError"
      [labelClassOverrides]="labelClassOverrides"
      [tooltipConfig]="tooltipConfig"
      [labelTextOptional]="labelTextOptional"
    ></e11-input-label>

    <div class="e11-flex e11-flex-wrap e11-gap-2 {{ inputClass }}">
      <ng-container *ngFor="let option of options">
        <e11-selector
          [color]="color"
          [loading]="loading"
          [hasCheckmark]="true"
          [label]="option.label"
          [selected]="hasOption(option)"
          [disabled]="option.disabled || disabled"
          [disableDisabledCursor]="loading ? true : false"
          (toggled)="toggleOption(option)"
          class="e11-cursor-pointer"
          [ngClass]="{
            'e11-skeleton e11-cursor-wait': loading,
          }"
        ></e11-selector>
      </ng-container>
    </div>

    <div *ngIf="hasError" class="e11-absolute e11-inset-y-0 e11-right-0 e11-pr-3 e11-flex e11-items-start e11-pointer-events-none">
      <e11-icon-error></e11-icon-error>
    </div>
  </div>`,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11MultiSelectorComponent),
                            multi: true,
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => E11MultiSelectorComponent),
                            multi: true,
                        },
                    ],
                    encapsulation: ViewEncapsulation.None,
                }]
        }], ctorParameters: () => [], propDecorators: { id: [{
                type: Input
            }], name: [{
                type: Input
            }], label: [{
                type: Input
            }], labelClassOverrides: [{
                type: Input
            }], inputClass: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], formControlName: [{
                type: Input
            }], options: [{
                type: Input
            }], value: [{
                type: Input
            }], hasError: [{
                type: Input
            }], required: [{
                type: Input
            }], disabled: [{
                type: Input
            }], loading: [{
                type: Input
            }], multi: [{
                type: Input
            }], tooltipConfig: [{
                type: Input
            }], labelTextOptional: [{
                type: Input
            }], color: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

class E11MultiSelectorModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MultiSelectorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11MultiSelectorModule, declarations: [E11MultiSelectorComponent], imports: [CommonModule, FormsModule, E11InputLabelModule, E11IconModule, E11SelectorModule], exports: [E11MultiSelectorComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MultiSelectorModule, imports: [CommonModule, FormsModule, E11InputLabelModule, E11IconModule, E11SelectorModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11MultiSelectorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11MultiSelectorComponent],
                    imports: [CommonModule, FormsModule, E11InputLabelModule, E11IconModule, E11SelectorModule],
                    exports: [E11MultiSelectorComponent],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11MultiSelectorComponent, E11MultiSelectorModule };
